import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

// ── Tier definitions ────────────────────────────────────────────────────────
// Only 'pro' exists beyond 'free' today.
// To add future tiers (e.g. 'enterprise'), extend this type and update
// the `tier` constant and gradient map below.

export type TierBadgeTier = 'pro';  // expand as new tiers arrive
export type TierBadgeVariant = 'solid' | 'outline';
export type TierBadgeSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'cosmos-tier-badge',
  templateUrl: './tier-badge.component.html',
  styleUrl: './tier-badge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TierBadgeComponent {
  /**
   * The tier this badge represents.
   * Commented out because there is only one tier right now — PRO.
   * Uncomment and bind dynamically once more tiers exist.
   */
  // readonly tier = input<TierBadgeTier>('pro');
  protected readonly tier: TierBadgeTier = 'pro';

  /**
   * The tier the current user/project actually holds.
   * When `currentTier` differs from `tier`, the label is prefixed with
   * "Only with " to signal that this feature is gated.
   */
  readonly currentTier = input<string>('free');

  readonly variant = input<TierBadgeVariant>('solid');
  readonly size    = input<TierBadgeSize>('md');

  private readonly translateService = inject(TranslateService);
  /** True when the current user does NOT hold this tier. */
  readonly isLocked = computed(() => this.currentTier() !== this.tier);

  readonly label = computed(() =>
    this.isLocked()
      ? `${this.translateService.instant('tiers.only-with')} ${this.tier.toUpperCase()}`
      : this.tier.toUpperCase()
  );

  readonly hostClasses = computed(() => ({
    'tier-badge': true,
    [`tier-badge--${this.variant()}`]: true,
    [`tier-badge--${this.size()}`]: true,
    [`tier-badge--${this.tier}`]: true,
    'tier-badge--locked': this.isLocked(),
  }));
}
